import type { ContactEntryType } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";
import createTrackingEventName from "app/pages/sales-intelligence/utils/testing/createTrackingEventName";
import type { CustomAttributes } from "app/services/track/track";
import type { track as TrackFunction } from "pro-features/components/WithTrack/src/WithTrack";

type SAPageVariant = "home" | "chat" | "gate";

const createSuperAgentTrackingService = (track: TrackFunction) => {
    const trackEvent = (action: string, eventName: string, customAttributes?: CustomAttributes): void => {
        track("SI AI Assistant", action, eventName, customAttributes);
    };

    const trackSAButtonClick = () => {
        trackEvent("click", "Open AI assistant");
    };

    const trackSendMessage = (value: string, pageVariant: SAPageVariant) => {
        trackEvent("send message", "chat/chat-composer/submit-prompt", {
            Value: value,
            Variant: pageVariant,
        });
    };

    const trackBreadcrumbClick = () => {
        trackEvent("click", "breadcrumbs navigation/home");
    };

    const trackLinkClick = (link: string | undefined, type: string) => {
        trackEvent("click", `chat/${type}/${link}`);
    };

    const trackNewChatClick = () => {
        trackEvent("click", "chat/chat-composer/new chat");
    };

    const trackCopyText = (value: string) => {
        const eventName = createTrackingEventName("chat", "manual copy", value);
        trackEvent("click", eventName);
    };

    const trackSignUpClick = () => {
        trackEvent("click", "Banner/Sign to beta");
    };

    const trackRecentAISearchClick = (value: string) => {
        trackEvent("click", "chat/chat-composer/recent prompts", { Value: value, Variant: "home" });
    };

    const trackSuggestionAISearchClick = (suggestion: string) => {
        trackEvent("click", "chat/chat-composer/suggestions", { Value: suggestion, Variant: "home" });
    };

    const sendPromptFromHomePage = (value: string) => {
        trackEvent("send message", "chat/chat-composer/submit-prompt", { Value: value, Variant: "home" });
    };

    const trackGatePageSuggestionClick = (suggestion: string) => {
        trackEvent("click", "chat/suggestion chips/suggestions", { Value: suggestion, Variant: "gate" });
    };

    const trackContactWidgetReveal = (variant: "table" | "card") => {
        trackEvent("click", `Contact widget/Reveal/${variant}`);
    };

    const trackContactEntryCopy = (entryType: ContactEntryType) => {
        trackEvent("click", `Contact widget/Copy/${entryType}`);
    };

    const trackEmailWidgetCopy = () => {
        trackEvent("click", "AI Outreach widget/Manual copy");
    };

    const trackEmailWidgetCopyButtonClick = () => {
        trackEvent("click", "AI Outreach widget/Copy");
    };

    const trackEmailWidgetLikeDislikeClick = (reaction: "Like" | "Dislike") => {
        trackEvent("click", `AI Outreach widget/${reaction}`);
    };

    return {
        trackSAButtonClick,
        trackSendMessage,
        trackBreadcrumbClick,
        trackLinkClick,
        trackRecentAISearchClick,
        trackSuggestionAISearchClick,
        sendPromptFromHomePage,
        trackNewChatClick,
        trackCopyText,
        trackSignUpClick,
        trackGatePageSuggestionClick,
        trackContactWidgetReveal,
        trackContactEntryCopy,
        trackEmailWidgetCopy,
        trackEmailWidgetCopyButtonClick,
        trackEmailWidgetLikeDislikeClick,
    };
};

export default createSuperAgentTrackingService;
